import axios from "axios";

import type { MessageTemplate, Category, CategoryResponse } from "./types";

import { useToast } from "vue-toastification";

const toast = useToast();

export const useMessageTemplates = defineStore('message-templates', () => {
    const items = ref<MessageTemplate[]>([]);
    const defaults = ref<MessageTemplate[]>([]);
    const categories = ref<Category[]>([]);

    // Loading states
    const isLoaded = ref<boolean>(false);
    const isLoading = ref<boolean>(false);

    /**
     * Get all message templates with the given category
     *
     * @param category
     */
    const filterByCategory = (category: 'Promotor' | 'Recommendation' | 'StatusChange') => {
        return items.value.filter(item => item.category === category);
    }

    /**
     * Load all message templates into items state
     */
    const load = async (forceReload: boolean = false) => {
        if(isLoaded.value && !forceReload) return;
        isLoading.value = true;

        try {
            await loadCategories();

            const response = await axios.get<{
                templates: MessageTemplate[],
                defaults: MessageTemplate[],
            }>('/api/message/templates');

            defaults.value = response.data.defaults;

            items.value = response.data.templates.sort((a, b) => {
                return (new Date(a.created_at)) < (new Date(b.created_at)) ? 1 : -1;
            });

            items.value.forEach(template => {
                template.getCategory = function (this: MessageTemplate) {
                    return categories.value.find(category => {
                        return this.category === category.value;
                    }) ?? categories[0];
                };

                template.getCategory.bind(template);
            });
        }
        catch(error) {
            toast.error("Fehler beim Laden der aktuellen Nachrichtenvorlagen");
        }

        isLoading.value = false;
        isLoaded.value = true;
    }

    /**
     * Load all available categories with their corresponding variable types
     * into categories state
     */
    const loadCategories = async () => {
        try {
            const response = await axios.get<{ categories: CategoryResponse }>('/api/message/categories');

            categories.value = Object.entries(response.data.categories).map(category => ({
                title: category[1].name,
                value: category[0],
                variables: Object.entries(category[1].variables).map(variable => ({
                    title: variable[1],
                    value: variable[0],
                })),
            }));
        }
        catch(error) {
            toast.error("Fehler beim Laden der Kategorien");

            throw error;
        }
    }

    /**
     * Create new message template
     *
     * @param template
     */
    const create = async (template: MessageTemplate) => {
        try {
            if(items.value.length > 0 && items.value[0].id < 0) {
                template.id = items.value[0].id - 1;
            }

            template.created_at = new Date().toISOString();
            template.updated_at = new Date().toISOString();

            items.value.unshift(template);

            updateDefault(template);

            const response = await axios.post<{ template: MessageTemplate }>(`/api/message/template`, template);

            const index = items.value.findIndex(item => item.id === template.id);

            response.data.template.getCategory = function (this: MessageTemplate) {
                return categories.value.find(category => {
                    return this.category === category.value;
                }) ?? categories[0];
            };

            response.data.template.getCategory.bind(response.data.template);

            items.value[index] = response.data.template;

            return true;
        }
        catch(error) {
            toast.error("Fehler beim Erstellen der Nachrichtenvorlage");

            await load(true);

            return false;
        }
    }

    /**
     * Update existing message template
     *
     * @param template
     */
    const update = async (template: MessageTemplate) => {
        try {
            const index = items.value.findIndex(item => item.id === template.id);

            template.updated_at = new Date().toISOString();

            items.value[index] = template;

            updateDefault(template);

            await axios.patch<{ template: MessageTemplate }>(`/api/message/template`, template);

            return true;
        }
        catch(error) {
            toast.error("Fehler beim Aktualisieren der Nachrichtenvorlage");

            await load(true);

            return false;
        }
    }

    /**
     * Delete existing message template
     *
     * @param template
     */
    const remove = async (template: MessageTemplate) => {
        try {
            const index = items.value.findIndex(item => item.id === template.id);

            items.value.splice(index, 1);

            await axios.delete<{ template: number }>(`/api/message/template`, { data: template });

            return true;
        }
        catch(error) {
            toast.error("Fehler beim Löschen der Nachrichtenvorlage");

            await load(true);

            return false;
        }
    }

    /**
     * Set the template default value of a template
     * with the same category to false if true
     *
     * @param updatedTemplate
     */
    const updateDefault = (updatedTemplate: MessageTemplate) => {
        if(!updatedTemplate.default) return;

        const template = items.value.find(template => {
            return template.default && template.category === updatedTemplate.category && template.id !== updatedTemplate.id;
        })

        if(template) {
            template.default = false;
        }
    }

    /**
     * Reset store to initial state
     */
    const reset = (): void => {
        items.value = [];
        defaults.value = [];
        categories.value = [];
        isLoaded.value = false;
        isLoading.value = false;
    };

    return { load, create, update, remove, filterByCategory, reset, items, defaults, categories, isLoading, isLoaded };
});